import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { ToWords } from 'to-words';
import { FinanceService } from 'src/app/services/finance.service';
import { MaterialModule } from 'src/app/shared/material.module';

pdfMake.vfs = pdfFonts.vfs;
const toWords = new ToWords();

@Component({
  selector: 'app-journal-voucher-print',
  standalone: true,
  imports: [CommonModule, MaterialModule, MatProgressBar],
  templateUrl: './journal-voucher-print.component.html',
  styleUrl: './journal-voucher-print.component.scss'
})
export class JournalVoucherPrintComponent implements OnInit, OnDestroy {
  voucherId!: number;
  voucherData: any;
  previewData: any = null;
  pdfDoc: any;
  isDialogMode = false;
  includeSignature = false;
  private originalHtmlOverflow = '';
  private originalBodyOverflow = '';

  constructor(
    private route: ActivatedRoute,
    private financeService: FinanceService,
    private snackBar: MatSnackBar,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { id?: number } | null,
    @Optional() private dialogRef: MatDialogRef<JournalVoucherPrintComponent> | null
  ) {}

  ngOnInit(): void {
    document.body.classList.add('invoice-preview-mode');
    this.originalHtmlOverflow = document.documentElement.style.overflow;
    this.originalBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    this.isDialogMode = !!this.dialogData?.id;
    this.voucherId = Number(this.dialogData?.id ?? this.route.snapshot.paramMap.get('id'));
    if (this.voucherId) {
      this.loadJournalVoucherData();
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('invoice-preview-mode');
    document.documentElement.style.overflow = this.originalHtmlOverflow;
    document.body.style.overflow = this.originalBodyOverflow;
  }

  printPdf() {
    this.pdfDoc?.print();
  }

  downloadPdf() {
    this.pdfDoc?.download(`Journal-Voucher-${this.previewData?.voucherNo || this.voucherId}.pdf`);
  }

  sendMail() {
    this.snackBar.open('Journal voucher mail template configured nahi mila. Mail compose open nahi kiya gaya.', 'Close', {
      duration: 4000
    });
  }

  onSignatureToggle(checked: boolean) {
    if (this.includeSignature === checked) {
      return;
    }

    this.includeSignature = checked;
    this.generatePdf();
  }

  closePreview() {
    this.dialogRef?.close();
  }

  private loadJournalVoucherData(): void {
    this.financeService.getJournalVoucherPrintById(this.voucherId).subscribe({
      next: (data: any) => {
        this.voucherData = Array.isArray(data) ? data[0] : data;
        this.previewData = this.buildPreviewData(this.voucherData || {});
        this.generatePdf();
      },
      error: (err) => console.error('Error loading journal voucher print', err)
    });
  }

  private buildPreviewData(source: any) {
    const company = this.pickEntity(source, ['company', 'companymaster', 'companydetails']);
    const branch = this.pickEntity(source, ['branch']);
    const header = source.header || source;
    const rawDetails = this.asArray(source.voucherdetails);

    const detailRows = rawDetails.map((row: any, index: number) => {
      const debit = Number(row.dramt || row.debitamount || 0);
      const credit = Number(row.cramt || row.creditamount || 0);
      return {
        sr: index + 1,
        ledgerName: this.safeValue(row.ledgername || row.accountname || row.name, row.ledgerid ? `Ledger ${row.ledgerid}` : '-'),
        branchName: this.safeValue(row.branchname || branch.branchname, '-'),
        entryType: row.isdebit === true ? 'Debit' : row.isdebit === false ? 'Credit' : debit > 0 ? 'Debit' : 'Credit',
        debitRaw: debit,
        creditRaw: credit,
        debitAmount: this.formatCurrency(debit, true),
        creditAmount: this.formatCurrency(credit, true)
      };
    });

    const totalDebit = Number(header.totaldebit ?? detailRows.reduce((sum: number, row: any) => sum + row.debitRaw, 0));
    const totalCredit = Number(header.totalcredit ?? detailRows.reduce((sum: number, row: any) => sum + row.creditRaw, 0));
    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

    return {
      logo: this.resolveImage([
        company.logoBase64,
        company.logobase64,
        company.logo,
        source.logoBase64,
        source.logobase64
      ]),
      signatureImage: this.resolveImage([
        company.signature,
        company.signatureBase64,
        company.signaturebase64,
        source.signature,
        source.signatureBase64
      ]),
      stampImage: this.resolveImage([
        company.stamp,
        company.stampBase64,
        company.stampbase64,
        source.stamp,
        source.stampBase64
      ]),
      companyInitials: this.getInitials(company.name),
      companyName: this.safeValue(company.name, 'VCS ERP'),
      companyNameTitle: this.safeValue(company.name, 'VCS ERP'),
      companyAddress: this.joinAddress([company.address1, company.address2, company.city, company.state, company.country, company.pincode]),
      companyMeta: this.buildCompanyMeta(company),
      signatoryName: this.safeValue(
        company.authorized_signatory_name ||
        company.authorizedSignatoryName ||
        source.authorized_signatory_name ||
        source.authorizedSignatoryName,
        ''
      ),
      signatoryTitle: this.safeValue(
        company.authorized_signatory_title ||
        company.authorizedSignatoryTitle ||
        source.authorized_signatory_title ||
        source.authorizedSignatoryTitle,
        'Authorized Signatory'
      ),
      voucherNo: this.safeValue(header.voucherno || header.code),
      voucherDate: this.formatDateValue(header.voucherdate),
      referenceNo: this.safeValue(header.partyrefinvoiceno, '-'),
      referenceDate: this.formatDateValue(header.partyrefinvoicedate),
      branchName: this.safeValue(branch.branchname, '-'),
      ledgerName: this.safeValue(source.ledger?.name || source.ledgername || '', '-'),
      totalDebit: this.formatCurrency(totalDebit, true),
      totalCredit: this.formatCurrency(totalCredit, true),
      isBalanced,
      overview: [
        { label: 'Branch', value: this.safeValue(branch.branchname, '-') },
        { label: 'Reference No', value: this.safeValue(header.partyrefinvoiceno, '-') },
        { label: 'Total Debit', value: this.formatCurrency(totalDebit, true) },
        { label: 'Total Credit', value: this.formatCurrency(totalCredit, true) }
      ],
      hasDetailRows: detailRows.length > 0,
      detailRows,
      remarks: this.safeValue(header.remarks, '-'),
      amountInWords: `${toWords.convert(Math.max(totalDebit, totalCredit), { currency: true })}/-`
    };
  }

  private generatePdf() {
    if (!this.previewData) {
      return;
    }

    const detailBody = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'LEDGER', style: 'tableHeader' },
        { text: 'BRANCH', style: 'tableHeader' },
        { text: 'ENTRY TYPE', style: 'tableHeader' },
        { text: 'DEBIT AMOUNT', style: 'tableHeader' },
        { text: 'CREDIT AMOUNT', style: 'tableHeader' }
      ],
      ...this.previewData.detailRows.map((row: any) => ([
        { text: String(row.sr), alignment: 'center' },
        { text: row.ledgerName },
        { text: row.branchName },
        { text: row.entryType, alignment: 'center' },
        { text: row.debitAmount, alignment: 'right' },
        { text: row.creditAmount, alignment: 'right' }
      ])),
      [
        { text: 'TOTAL', colSpan: 4, alignment: 'right', bold: true, fillColor: '#eef2ff', margin: [0, 4, 8, 4] },
        {}, {}, {},
        { text: this.previewData.totalDebit, alignment: 'right', bold: true, fillColor: '#eef2ff' },
        { text: this.previewData.totalCredit, alignment: 'right', bold: true, fillColor: '#eef2ff' }
      ]
    ];

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [20, 12, 20, 42],
      footer: (currentPage: number, pageCount: number) => ({
        margin: [20, 0, 20, 6],
        stack: [
          {
            canvas: [
              { type: 'line', x1: 0, y1: 0, x2: 555, y2: 0, lineWidth: 1, color: '#8da0b8' },
              { type: 'rect', x: 0, y: 5, w: 132, h: 2.6, color: '#153a6b' },
              { type: 'rect', x: 441, y: 5, w: 114, h: 2.6, color: '#d97706' }
            ]
          },
          {
            margin: [0, 6, 0, 0],
            columns: [
              { width: '*', text: 'Generated using VCS ERP', style: 'footerText' },
              { width: 90, text: `Page ${currentPage} of ${pageCount}`, style: 'footerPageText', alignment: 'right' }
            ]
          }
        ]
      }),
      background: this.previewData.logo
        ? { image: this.previewData.logo, alignment: 'center', width: 300, height: 300, opacity: 0.05, marginTop: 220 }
        : undefined,
      content: [
        {
          canvas: [
            { type: 'rect', x: 0, y: 0, w: 551, h: 6, color: '#153a6b' },
            { type: 'rect', x: 405, y: 0, w: 146, h: 6, color: '#d97706' },
            { type: 'line', x1: 0, y1: 15, x2: 551, y2: 15, lineWidth: 0.7, color: '#e2e8f0' }
          ],
          margin: [0, -10, 0, 4]
        },
        {
          table: {
            widths: [74, '*', 168],
            body: [[
              {
                stack: this.previewData.logo
                  ? [{ image: this.previewData.logo, width: 66, fit: [66, 66], alignment: 'center' }]
                  : [{ text: this.previewData.companyInitials, style: 'logoFallback' }],
                border: [false, false, false, false],
                margin: [0, 6, 0, 0]
              },
              {
                stack: [
                  { text: this.previewData.companyName.toUpperCase(), style: 'companyHeader' },
                  this.previewData.companyAddress ? { text: this.previewData.companyAddress, style: 'companyMeta' } : '',
                  ...this.previewData.companyMeta.map((text: string) => ({ text, style: 'companyMeta' }))
                ].filter(Boolean),
                border: [false, false, false, false],
                margin: [0, 4, 0, 0]
              },
              {
                stack: [
                  { text: 'JOURNAL VOUCHER', style: 'invoiceHeaderBadge' },
                  { text: `Voucher No: ${this.previewData.voucherNo}`, style: 'invoiceMetaStrong' },
                  { text: `Voucher Date: ${this.previewData.voucherDate}`, style: 'invoiceMeta' },
                  { text: `Reference No: ${this.previewData.referenceNo}`, style: 'invoiceMeta' },
                  { text: `Reference Date: ${this.previewData.referenceDate}`, style: 'invoiceMeta' }
                ],
                fillColor: '#f6f8ff',
                margin: [10, 8, 10, 8],
                border: [true, true, true, true]
              }
            ]]
          },
          layout: {
            hLineColor: () => '#d7deea',
            vLineColor: () => '#d7deea'
          }
        },
        {
          columns: this.previewData.overview.map((card: any) => ({
            width: '*',
            stack: [
              { text: card.label, style: 'cardTitle' },
              { text: card.value, style: 'cardHeading' }
            ],
            margin: [8, 8, 8, 8],
            fillColor: '#f8fafc'
          })),
          columnGap: 12,
          margin: [0, 10, 0, 8]
        },
        ...(this.previewData.hasDetailRows ? [
          { text: 'Voucher Details', style: 'sectionHeader' },
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              widths: [28, '*', 90, 70, 86, 86],
              body: detailBody
            },
            layout: this.buildTableLayout()
          }
        ] : []),
        {
          margin: [0, 10, 0, 0],
          columns: [
            {
              width: '*',
              stack: [
                { text: 'Amount In Words', style: 'sectionHeader' },
                { text: this.previewData.amountInWords, style: 'cardText' },
                ...(this.previewData.remarks !== '-' ? [
                  { text: 'Remarks', style: 'sectionHeader', margin: [0, 12, 0, 0] },
                  { text: this.previewData.remarks, style: 'cardText' }
                ] : [])
              ]
            },
            {
              width: 188,
              unbreakable: true,
              stack: [
                {
                  margin: [0, 50, 0, 0],
                  unbreakable: true,
                  stack: [
                    { text: `For ${this.previewData.companyNameTitle}`, alignment: 'right', bold: true, color: '#153a6b' },
                    ...(this.includeSignature
                      ? [{
                          columns: [
                            { width: '*', text: '' },
                            {
                              width: 'auto',
                              stack: [
                                {
                                  columns: [
                                    ...(this.previewData.stampImage ? [{ width: 70, image: this.previewData.stampImage, fit: [70, 70], margin: [0, 4, 6, 0] }] : []),
                                    ...(this.previewData.signatureImage ? [{ width: 74, image: this.previewData.signatureImage, fit: [74, 34], margin: [0, 22, 0, 0] }] : [])
                                  ]
                                }
                              ]
                            }
                          ],
                          margin: [0, 8, 0, 0]
                        }]
                      : []),
                    { text: this.previewData.signatoryName || '', alignment: 'right', margin: [0, 12, 0, 0], fontSize: 8.6, bold: true },
                    { text: this.previewData.signatoryTitle || 'Authorized Signatory', alignment: 'right', fontSize: 8.6, color: '#475569' }
                  ]
                }
              ]
            }
          ]
        }
      ],
      styles: {
        companyHeader: { fontSize: 18, bold: true, color: '#0f2852', alignment: 'center' },
        companyMeta: { fontSize: 10.2, color: '#2b3e5f', alignment: 'center', margin: [0, 1, 0, 0] },
        invoiceHeaderBadge: { fontSize: 11, bold: true, color: '#0f2852', margin: [0, 0, 0, 8] },
        invoiceMetaStrong: { fontSize: 9.4, bold: true, color: '#12253f', margin: [0, 2, 0, 0] },
        invoiceMeta: { fontSize: 9, color: '#334155', margin: [0, 2, 0, 0] },
        cardTitle: { fontSize: 9, bold: true, color: '#48627f', margin: [0, 0, 0, 6] },
        cardHeading: { fontSize: 12, bold: true, color: '#0f2852' },
        cardText: { fontSize: 9.1, color: '#334155', margin: [0, 2, 0, 0] },
        tableHeader: { fontSize: 8.7, bold: true, color: '#ffffff', fillColor: '#17354d', alignment: 'center', margin: [0, 4, 0, 4] },
        sectionHeader: { fontSize: 11.2, bold: true, color: '#0b7285', margin: [0, 0, 0, 6] },
        footerText: { fontSize: 8, color: '#64748b' },
        footerPageText: { fontSize: 8, color: '#334155', bold: true },
        logoFallback: {
          fontSize: 26,
          bold: true,
          color: '#153a6b',
          alignment: 'center',
          margin: [0, 16, 0, 0]
        }
      },
      defaultStyle: {
        fontSize: 9,
        color: '#0f172a'
      }
    };

    this.pdfDoc = pdfMake.createPdf(docDefinition);
  }

  private buildTableLayout() {
    return {
      hLineColor: () => '#b9c7d8',
      vLineColor: () => '#b9c7d8',
      hLineWidth: () => 0.7,
      vLineWidth: () => 0.7,
      fillColor: (rowIndex: number) => rowIndex === 0 ? '#17354d' : rowIndex % 2 === 0 ? '#f8fafc' : null
    };
  }

  private pickEntity(source: any, keys: string[]) {
    for (const key of keys) {
      if (source?.[key]) {
        return source[key];
      }
    }
    return {};
  }

  private buildCompanyMeta(company: any): string[] {
    const gstLine = this.metaLine('GSTIN', company.gstin || company.gstnumber, '');
    const panLine = this.metaLine('PAN', company.pan || company.panno, '');
    const emailLine = this.metaLine('Email', company.email || company.emailid, '');
    const websiteLine = this.metaLine('Website', company.website, '');

    return [
      [gstLine, panLine].filter(Boolean).join('    '),
      [emailLine, websiteLine].filter(Boolean).join('    ')
    ].filter(Boolean);
  }

  private metaLine(label: string, value: any, fallback = ''): string {
    const cleaned = this.safeValue(value, '');
    return cleaned ? `${label}: ${cleaned}` : fallback;
  }

  private formatDateValue(value: any): string {
    if (!value) {
      return '-';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.safeValue(value, '-');
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  private formatCurrency(value: any, showSymbol = true): string {
    const amount = Number(value || 0);
    const formatter = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return showSymbol ? `₹ ${formatter.format(amount)}` : formatter.format(amount);
  }

  private joinAddress(parts: any[]): string {
    return parts
      .map((part) => this.safeValue(part, ''))
      .filter(Boolean)
      .join(', ');
  }

  private getInitials(name: string): string {
    return this.safeValue(name, 'VC')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('');
  }

  private resolveImage(candidates: any[]): string | null {
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'string') {
        continue;
      }

      const trimmed = candidate.trim();
      if (!trimmed) {
        continue;
      }

      if (trimmed.startsWith('data:image')) {
        return trimmed;
      }

      return `data:image/png;base64,${trimmed}`;
    }

    return null;
  }

  private safeValue(value: any, fallback = '-'): string {
    if (value === null || value === undefined) {
      return fallback;
    }

    const text = String(value).trim();
    return text ? text : fallback;
  }

  private asArray(value: any): any[] {
    return Array.isArray(value) ? value : [];
  }
}
